import { mixpanelTrack } from "./mixpanelTrack";

// Evolutions client-side Mixpanel events. Pure-client interactions (view /
// explore / sort / filter / quick_filter / upgrade) live here; data-mutating
// actions (save evolution state, save evo player) are fired server-side in
// peareasy-services instead.
//
// Mirrors events/squadBuilderMixpanel.ts: routes through the shared
// mixpanelTrack helper, a SECTION constant, and one track* fn per event. Props
// (subsection, filters, sort value, ...) are read from redux selectors at the
// call site and passed in.

const SECTION = "Evolutions";

export type EvolutionsSubsection =
  | "Overview"
  | "Evo View"
  | "Planned Evolutions"
  | "My Evolutions"
  | "Builder"
  | "Builder Overview"
  | "PlayStyles Lab"
  | "Players"
  | "Player View";

type EvolutionsContentType = "evolution" | "evo_player";

export const trackViewContent = (params: {
  subsection: EvolutionsSubsection;
  content_type: EvolutionsContentType;
  new_content?: boolean;
  // "core" for direct content opens, "browse" when reached from a recommended
  // / inspiration card (see the tracking plan per subsection).
  action_type?: "core" | "browse";
}) => {
  mixpanelTrack("view_content", {
    section: SECTION,
    subsection: params.subsection,
    content_type: params.content_type,
    new_content: params.new_content ?? false,
    action_type: params.action_type ?? "core",
  });
};

export const trackExplore = (params: {
  // Origin section. Defaults to "Evolutions"; the standalone /players page fires
  // this from the "Players" section instead (destination is still Evolutions).
  section?: string;
  subsection: EvolutionsSubsection;
  source: string;
  destination_section: string;
  destination_subsection: string;
  new_content?: boolean;
}) =>
  mixpanelTrack("explore", {
    section: params.section ?? SECTION,
    subsection: params.subsection,
    source: params.source,
    destination_section: params.destination_section,
    destination_subsection: params.destination_subsection,
    new_content: params.new_content ?? false,
    action_type: "browse",
  });

export const trackSort = (params: {
  subsection: EvolutionsSubsection;
  value: string;
}) => {
  const value = params.value === "date-added" ? "new" : params.value;
  mixpanelTrack("sort", {
    section: SECTION,
    subsection: params.subsection,
    value,
    action_type: "browse",
  });
};
// Fired when the user closes the filters panel. `filters` is the list of active
// filter keys; an Evolutions-specific filter collapses to ["evolutions"]
// regardless of how many evolution filters changed (per the tracking plan).
export const trackFilter = (params: {
  subsection: EvolutionsSubsection;
  filters: string[];
}) =>
  mixpanelTrack("filter", {
    section: SECTION,
    subsection: params.subsection,
    filters: params.filters,
    action_type: "browse",
  });

// Single quick-filter key from the bar ("my club" / "my evolutions" / "squad"
// / "chain tag").
export const trackQuickFilter = (params: {
  subsection: EvolutionsSubsection;
  filters: string;
}) =>
  mixpanelTrack("quick_filter", {
    section: SECTION,
    subsection: params.subsection,
    filters: params.filters,
    action_type: "browse",
  });

export const trackUpgrade = (params: {
  subsection: EvolutionsSubsection;
  context: "filter" | "cta";
}) =>
  mixpanelTrack("upgrade", {
    section: SECTION,
    subsection: params.subsection,
    context: params.context,
    action_type: "lifecycle",
  });

// `import_club` now comes from events/clubImportMixpanel, fired once at the
// launch chokepoint with this section as its origin.

export const trackAddedPlayerToEvolutionBuilder = (params: {
  subsection: EvolutionsSubsection;
}) =>
  mixpanelTrack("added_player_to_evolution_builder", {
    section: SECTION,
    subsection: params.subsection,
    action_type: "browse",
  });

// Builder-only core actions. Fired client-side at the add handler (the only API
// they touch is a shared recompute endpoint that fires on every chain change,
// so it isn't a valid server-side home).
export const trackAddedEvolutionStep = () =>
  mixpanelTrack("added_evolution_step", {
    section: SECTION,
    subsection: "Builder",
    action_type: "core",
  });

export const trackAddedChainRecommendation = () =>
  mixpanelTrack("added_chain_recommendation", {
    section: SECTION,
    subsection: "Builder",
    action_type: "core",
  });

// No share API persists evo players (unlike squads); the only backend route is
// per-image generation. Fired client-side at the share click instead.
export const trackShare = (params: {
  subsection: EvolutionsSubsection;
  content_type?: "evo_player";
}) =>
  mixpanelTrack("share", {
    section: SECTION,
    subsection: params.subsection,
    content_type: params.content_type ?? "evo_player",
    action_type: "community",
  });
